<style lang="scss" scoped>
@import "./management.modal.scss";
</style>

<template>
    <cc-modal :modal="modal" @close="close">
        <div slot="body" class="container-fluid pt-0">
            <div v-if="!showForm && !showLogs" class="integration-management__actions-container">
                <Button :iconName="'add_circle'" title="Nova Integração" :text="'Nova Integração'" :action="openCreateForm" />
            </div>

            <div v-if="!showForm && !showLogs">
                <table class="table">
                    <thead>
                        <tr class="">
                            <th class="name-width">Nome</th>
                            <th class="type-width">Tipo</th>
                            <th class="status-width">Status</th>
                            <th class="actions-width">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="integrations.length === 0">
                            <td colspan="4" class="empty-state">
                                Nenhuma integração cadastrada
                            </td>
                        </tr>

                        <tr
                            v-for="(integration, idx) in integrations"
                            :key="integration.id"
                            :class="[{ 'page-table-line-special': idx % 2 !== 0 }]"
                        >
                            <td class="name-width">
                                {{ integration.name }}
                            </td>
                            <td class="type-width">
                                {{ integration.integration_type }}
                            </td>
                            <td class="status-width">
                                <span
                                    class="status-badge"
                                    :class="integration.is_active ? 'status-active' : 'status-inactive'"
                                    @click="toggleIntegration(integration)"
                                    style="cursor: pointer;"
                                >
                                    {{ integration.is_active ? "Ativo" : "Inativo" }}
                                </span>
                            </td>
                            <td class="actions-width">
                                <span
                                    v-if="integration.integration_type === 'WINTHOR'"
                                    class="material-icons-outlined sync-icon"
                                    title="Sincronizar Produtos"
                                    @click="syncProducts(integration)"
                                >
                                    sync
                                </span>
                                <span class="material-icons-outlined edit-icon" title="Editar" @click="editIntegration(integration)">
                                    edit
                                </span>
                                <span class="material-icons-outlined view-icon" title="Logs" @click="viewLogs(integration)">
                                    description
                                </span>
                                <span class="material-icons-outlined delete-icon" title="Excluir" @click="deleteIntegration(integration)">
                                    delete
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="showForm">
                <IntegrationForm ref="form" :integration="currentIntegration" :entityType="entityType" />
            </div>

            <div v-if="showLogs">
                <IntegrationLogs
                    :integration="currentIntegration"
                    :logs="integrationLogs"
                    :pagination="logsPagination"
                    @close="closeLogs"
                    @page-change="loadLogsPage"
                />
            </div>
        </div>
    </cc-modal>
</template>

<script>
import IntegrationService from "@/services/v3/integration.service";
import ProviderProductService from "@/services/v2/provider-product.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import Button from "@/components/ui/buttons/StandardButton.vue";
import IntegrationForm from "./_form.component.vue";
import IntegrationLogs from "./_logs.component.vue";

export default {
    name: "IntegrationManagementModal",
    mixins: [loaderMixin],
    components: {
        Button,
        IntegrationForm,
        IntegrationLogs,
    },
    props: {
        client: {
            type: Object,
            required: false,
            default: null,
        },
        provider: {
            type: Object,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            integrations: [],
            currentIntegration: {
                name: "",
                integration_type: "",
                description: "",
                configuration: {},
                is_active: true,
            },
            showForm: false,
            showLogs: false,
            integrationLogs: [],
            logsPagination: {
                current_page: 1,
                per_page: 20,
                total: 0,
            },
            integration_svc: new IntegrationService(),
            provider_product_svc: new ProviderProductService(),
        };
    },
    computed: {
        entity() {
            return this.client || this.provider;
        },
        entityType() {
            return this.client ? "client" : "provider";
        },
        modal() {
            const config = {
                title: "gerenciar integrações",
                subtitle: this.showLogs ? "Visualize os logs de integração" : "Configure as integrações de terceiros para o cliente",
                icon_title: "fas fa-plug",
                cancel_text: "Voltar",
                permitted: this.formValid,
                style: {
                    width: "90%",
                    height: "700px",
                },
            };

            if (this.showForm) {
                config.submit = this.saveIntegration;
            }

            return config;
        },
        formValid() {
            if (!this.showForm || !this.$refs.form || !this.$refs.form.$refs.form) {
                return true;
            }
            return this.$refs.form.$refs.form.flags.valid;
        },
        isVarejoFacil() {
            return this.currentIntegration.integration_type === "VAREJO_FACIL" || this.currentIntegration.integration_type === "TARGET";
        },
    },
    watch: {
        "currentIntegration.integration_type"(newType) {
            // Only set default values for new integrations, not when editing
            if (this.currentIntegration.id) {
                return;
            }

            if (newType === "VAREJO_FACIL") {
                this.currentIntegration.name = "Varejofacil";
                this.currentIntegration.configuration = {
                    subdomain: "your-subdomain",
                    api_key: "your-api-key",
                    operation_id: 1,
                    username: "your-username",
                    password: "your-password",
                };
            } else if (newType === "TARGET") {
                this.currentIntegration.name = "Target";
                this.currentIntegration.configuration = {
                    url: "https://api.target.com",
                    username: "your-username",
                    password: "your-password",
                };
            } else if (newType === "WHATSAPP") {
                this.currentIntegration.name = "WhatsApp";
                this.currentIntegration.configuration = {
                    on_request_created: false,
                    on_order_created: false,
                    on_order_canceled: false,
                    on_invitation_created: false,
                };
            } else if (newType === "APS") {
                this.currentIntegration.name = "Aps";
                this.currentIntegration.configuration = {};
            } else if (newType === "RMS") {
                this.currentIntegration.name = "Rms";
                this.currentIntegration.configuration = {
                    base_url: "",
                    api_key: "",
                };
            } else if (newType === "WINTHOR") {
                this.currentIntegration.name = "Winthor";
                this.currentIntegration.configuration = {
                    url: "",
                    login: "",
                    senha: "",
                    branch_id: "",
                };
            } else {
                this.currentIntegration.name = "";
                this.currentIntegration.configuration = {};
            }
        },
    },
    mounted() {
        this.loadIntegrations();
    },
    methods: {
        close() {
            if (this.showForm) {
                this.cancelForm();
            } else if (this.showLogs) {
                this.closeLogs();
            } else {
                this.$emit("close");
            }
        },
        async loadIntegrations() {
            try {
                this.present_loader("Carregando integrações...");
                const params = {};
                if (this.client) {
                    params.client_id = this.client.id;
                } else if (this.provider) {
                    params.provider_id = this.provider.id;
                }
                const response = await this.integration_svc.list(params);
                const integrations = response.data.data || response.data;

                // Ensure configuration is parsed if it comes as JSON string
                this.integrations = integrations.map((integration) => ({
                    ...integration,
                    configuration:
                        typeof integration.configuration === "string"
                            ? JSON.parse(integration.configuration)
                            : integration.configuration || {},
                }));

                this.dismiss_loader();
            } catch (error) {
                this.dismiss_loader();
                ErrorHandlerService.handle(error, this.$store);
            }
        },

        openCreateForm() {
            const integrationType = this.entityType === "client" ? "VAREJO_FACIL" : "TARGET";
            const integrationName = this.entityType === "client" ? "Varejofacil" : "Target";
            const defaultConfig =
                this.entityType === "client"
                    ? {
                          subdomain: "your-subdomain",
                          api_key: "your-api-key",
                          operation_id: 1,
                          username: "your-username",
                          password: "your-password",
                      }
                    : { url: "https://api.target.com", username: "your-username", password: "your-password" };

            this.currentIntegration = {
                name: integrationName,
                integration_type: integrationType,
                description: "",
                configuration: defaultConfig,
                is_active: true,
            };
            this.showForm = true;
        },

        editIntegration(integration) {
            // Deep clone to avoid reactivity issues
            const config =
                typeof integration.configuration === "string" ? JSON.parse(integration.configuration) : integration.configuration || {};

            this.currentIntegration = {
                ...integration,
                configuration: this.normalizeWhatsappConfig(integration.integration_type, config),
            };
            this.showForm = true;
        },

        cancelForm() {
            this.showForm = false;
            this.currentIntegration = {
                name: "",
                integration_type: "",
                description: "",
                configuration: {},
                is_active: true,
            };
        },

        async saveIntegration() {
            try {
                // Validate form before submitting
                if (this.$refs.form && this.$refs.form.$refs.form) {
                    const isValid = await this.$refs.form.$refs.form.validate();
                    if (!isValid) {
                        this.$swal.fire("Atenção!", "Preencha todos os campos obrigatórios.", "warning");
                        return;
                    }
                }

                this.present_loader("Salvando integração...");

                const data = {
                    ...this.currentIntegration,
                    configuration: this.normalizeWhatsappConfig(
                        this.currentIntegration.integration_type,
                        this.currentIntegration.configuration || {}
                    ),
                };

                if (this.client) {
                    data.client_id = this.client.id;
                } else if (this.provider) {
                    data.provider_id = this.provider.id;
                }

                if (this.currentIntegration.id) {
                    await this.integration_svc.update(this.currentIntegration.id, data);
                    this.$swal.fire("Sucesso!", "Integração atualizada com sucesso.", "success");
                } else {
                    await this.integration_svc.create(data);
                    this.$swal.fire("Sucesso!", "Integração criada com sucesso.", "success");
                }

                this.dismiss_loader();
                this.cancelForm();
                await this.loadIntegrations();
            } catch (error) {
                this.dismiss_loader();
                ErrorHandlerService.handle(error, this.$store);
            }
        },

        async toggleIntegration(integration) {
            try {
                this.present_loader("Atualizando status...");
                await this.integration_svc.toggle(integration.id);
                this.$swal.fire("Sucesso!", `Integração ${integration.is_active ? "desativada" : "ativada"} com sucesso.`, "success");
                await this.loadIntegrations();
                this.dismiss_loader();
            } catch (error) {
                this.dismiss_loader();
                ErrorHandlerService.handle(error, this.$store);
            }
        },

        async deleteIntegration(integration) {
            const result = await this.$swal.fire({
                title: "Confirma exclusão?",
                text: "Esta ação não pode ser desfeita.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Sim, excluir!",
                cancelButtonText: "Cancelar",
            });

            if (result.isConfirmed) {
                try {
                    this.present_loader("Excluindo integração...");
                    await this.integration_svc.destroy(integration.id);
                    this.$swal.fire("Excluído!", "Integração excluída com sucesso.", "success");
                    await this.loadIntegrations();
                    this.dismiss_loader();
                    this.close();
                } catch (error) {
                    this.dismiss_loader();
                    ErrorHandlerService.handle(error, this.$store);
                }
            }
        },

        normalizeWhatsappConfig(integrationType, configuration) {
            if (integrationType !== "WHATSAPP") {
                return configuration;
            }

            const requestFlag = Boolean(configuration.on_request_created);

            return {
                ...configuration,
                on_request_created: requestFlag,
                on_order_created: Boolean(configuration.on_order_created),
                on_order_canceled: Boolean(configuration.on_order_canceled),
                on_invitation_created:
                    typeof configuration.on_invitation_created === "boolean" ? configuration.on_invitation_created : requestFlag,
            };
        },

        async viewLogs(integration) {
            this.currentIntegration = integration;
            this.showLogs = true;
            await this.loadLogsPage(1);
        },

        async loadLogsPage(page) {
            try {
                this.present_loader("Carregando logs...");
                const response = await this.integration_svc.logs(this.currentIntegration.id, {
                    page: page,
                    per_page: this.logsPagination.per_page,
                });

                const data = response.data;
                this.integrationLogs = data.data || [];

                if (data.meta) {
                    this.logsPagination = {
                        current_page: data.meta.current_page,
                        per_page: data.meta.per_page,
                        total: data.meta.total,
                    };
                }

                this.dismiss_loader();
            } catch (error) {
                this.dismiss_loader();
                ErrorHandlerService.handle(error, this.$store);
            }
        },

        closeLogs() {
            this.showLogs = false;
            this.integrationLogs = [];
            this.logsPagination = {
                current_page: 1,
                per_page: 20,
                total: 0,
            };
            this.currentIntegration = {
                name: "",
                integration_type: "",
                description: "",
                configuration: {},
                is_active: true,
            };
        },

        async syncProducts(integration) {
            const result = await this.$swal.fire({
                title: "Sincronizar produtos do Winthor?",
                text: "Isso irá importar e atualizar os produtos a partir do ERP Winthor.",
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Sim, sincronizar",
                cancelButtonText: "Cancelar",
            });

            if (result.isConfirmed) {
                try {
                    this.present_loader("Sincronizando produtos...");
                    await this.provider_product_svc.syncWinthorProducts(this.entity.id, integration.id);
                    this.dismiss_loader();
                    this.$swal.fire("Sucesso!", "Sincronização de produtos agendada com sucesso.", "success");
                } catch (error) {
                    this.dismiss_loader();
                    ErrorHandlerService.handle(error, this.$store);
                }
            }
        },

        async viewStatistics(integration) {
            try {
                this.present_loader("Carregando estatísticas...");
                const response = await this.integration_svc.statistics(integration.id);
                this.dismiss_loader();

                // TODO: Implement statistics view - for now just show a message
                this.$swal.fire({
                    title: "Estatísticas da Integração",
                    text: `Estatísticas para: ${integration.name}`,
                    icon: "info",
                });
            } catch (error) {
                this.dismiss_loader();
                ErrorHandlerService.handle(error, this.$store);
            }
        },
    },
};
</script>
